/**
 * MatrixField — Northstar Stage 3 Refinement (§7).
 *
 * Course-correction pass (2026-02):
 *   - Density substantially increased (finer column pitch, more streams,
 *     varied lengths/opacity/speed, occasional brighter "hot" characters,
 *     small glyph clusters).
 *   - Outer-weighted attenuation preserved: near-absent through the
 *     center of the composition; densest in the outer 35–45% on each
 *     side of the viewport. The center must remain visually quiet so
 *     the person + rings + connections stay dominant.
 *   - Multiple particle types share this single canvas (streams +
 *     ambient dust) to satisfy the composition brief without adding
 *     another rAF loop.
 *
 * Reduced motion: renders a single dense static frame and stops.
 * Never katakana — glyphs are abstract digital tokens.
 */
import React, { useEffect, useRef } from "react";

const CHARS =
  "0123456789.:;{}/\\|+<>=#*·⌇⌘⋅◇◦░▓▪▫⧉⌁⌥⌒".split("");

export default function MatrixField({
  reducedMotion = false,
  innerRatio = 0.22,
  outerRatio = 0.48,
}) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d", { alpha: true });
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    let width = 0;
    let height = 0;
    let streams = [];
    let dust = [];
    let clusters = [];
    let rafId = null;
    let running = true;

    const measure = () => {
      const rect = canvas.parentElement
        ? canvas.parentElement.getBoundingClientRect()
        : { width: window.innerWidth, height: window.innerHeight };
      width = Math.max(1, Math.floor(rect.width));
      height = Math.max(1, Math.floor(rect.height));
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = width + "px";
      canvas.style.height = height + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const alphaFor = (x) => {
      const dx = Math.abs(x - width / 2);
      const inner = width * innerRatio;
      const outer = width * outerRatio;
      // Continuous field: every column carries a small baseline alpha
      // so the environment reads as a single data field rather than
      // two decorative strips. The baseline is intentionally tiny —
      // near-invisible over the face — and ramps quadratically out.
      const baseline = 0.05;
      if (dx <= inner) return baseline * (dx / Math.max(inner, 1));
      if (dx >= outer) return 1;
      const t = (dx - inner) / (outer - inner);
      return baseline + (1 - baseline) * t * t;
    };

    const buildStreams = () => {
      const step = 10;  // denser pitch (~1.5x vs previous)
      streams = [];
      for (let x = step / 2; x < width; x += step) {
        const alpha = alphaFor(x);
        if (alpha < 0.03) continue;
        // Ambient centre-band streams remain very sparse; outer
        // streams populate freely.
        if (Math.random() > 0.42 + 0.55 * alpha) continue;
        streams.push({
          x: x + (Math.random() - 0.5) * 4, // slight jitter
          y: Math.random() * height * 1.2 - height * 0.1,
          speed: 1.1 + Math.random() * 3.8,
          alpha,
          length: 5 + Math.floor(Math.random() * 16),
          hotEvery: 30 + Math.floor(Math.random() * 90),
          // Interrupted streams: fade out partway then restart.
          interrupt: Math.random() < 0.28
            ? { at: 0.3 + Math.random() * 0.5, span: 40 + Math.random() * 90 }
            : null,
          age: 0,
        });
      }
      // A second thin "shadow" layer of very faint continuous streams
      // that overlap the main streams — gives the field vertical
      // continuity without brightening it.
      const shadowStep = 22;
      for (let x = shadowStep / 2 + 4; x < width; x += shadowStep) {
        const alpha = alphaFor(x) * 0.55;
        if (alpha < 0.03) continue;
        if (Math.random() > 0.55 + 0.35 * alpha) continue;
        streams.push({
          x: x + (Math.random() - 0.5) * 3,
          y: Math.random() * height,
          speed: 0.7 + Math.random() * 1.6,
          alpha: alpha * 0.6,
          length: 3 + Math.floor(Math.random() * 8),
          hotEvery: 120,
          interrupt: null,
          age: 0,
        });
      }
      // Static glyph clusters — small 2×2 to 3×3 groups of characters
      // scattered only in the outermost band so they never overlap
      // typography. They flicker (see `phase`) so the eye reads them
      // as data, not decoration.
      clusters = [];
      const clusterCount = Math.floor(width * height / 40000);
      for (let i = 0; i < clusterCount; i++) {
        const x = Math.random() * width;
        const y = Math.random() * height;
        const alpha = alphaFor(x);
        if (alpha < 0.82) continue;
        // Header + footer exclusion: no clusters in the top-104px
        // band (any tier's header reserve fits inside that) or the
        // bottom 24 px (they would flicker just above the viewport
        // edge). Keeps chrome visually clean.
        if (y < 104 || y > height - 30) continue;
        clusters.push({
          x,
          y,
          w: 2 + Math.floor(Math.random() * 2),  // 2-3 columns
          h: 2 + Math.floor(Math.random() * 3),  // 2-4 rows
          alpha: alpha * (0.30 + Math.random() * 0.30),
          phase: Math.random() * Math.PI * 2,
          period: 1800 + Math.random() * 2400,
        });
      }
      // Ambient static dust — a bit more of it for atmospheric depth.
      dust = [];
      const dustCount = Math.floor(width * height / 16000);
      for (let i = 0; i < dustCount; i++) {
        const x = Math.random() * width;
        const alpha = alphaFor(x);
        if (alpha < 0.08) continue;
        dust.push({
          x,
          y: Math.random() * height,
          alpha: alpha * (0.35 + Math.random() * 0.55),
          twinkle: Math.random() * Math.PI * 2,
        });
      }
    };

    const drawFrame = (t) => {
      if (!running) return;
      // Trail fade
      ctx.fillStyle = "rgba(4,8,22,0.16)";
      ctx.fillRect(0, 0, width, height);
      ctx.font = "12px ui-monospace, SFMono-Regular, Menlo, monospace";
      ctx.textBaseline = "alphabetic";

      // Streams — head glyph + short trail behind it.
      for (const s of streams) {
        s.age += 1;
        // Interrupted streams: the trail vanishes for ~span pixels at
        // a fixed y-position derived from `at` and springs back. This
        // produces the "flowing information" reading instead of
        // uniform code rain.
        const interruptWindow = (yPos) => {
          if (!s.interrupt) return 1;
          const iy = s.interrupt.at * height;
          const dy = Math.abs(yPos - iy);
          if (dy > s.interrupt.span) return 1;
          return Math.min(1, dy / s.interrupt.span);
        };
        // Trail
        for (let k = 0; k < s.length; k++) {
          const y = s.y - k * 14;
          if (y < -14 || y > height + 14) continue;
          const ch = CHARS[(Math.random() * CHARS.length) | 0];
          const trailA = s.alpha * Math.max(0, 1 - k / s.length) * 0.55 * interruptWindow(y);
          if (trailA < 0.02) continue;
          ctx.fillStyle = `rgba(126,212,147,${trailA})`;
          ctx.fillText(ch, s.x, y);
        }
        // Head — occasional brighter "hot" character
        const hot = s.age % s.hotEvery === 0;
        const head = CHARS[(Math.random() * CHARS.length) | 0];
        const headA = interruptWindow(s.y);
        if (headA > 0.05) {
          ctx.fillStyle = hot
            ? `rgba(223,247,230,${Math.min(1, s.alpha * 0.9 * headA)})`
            : `rgba(184,239,198,${Math.min(1, s.alpha * 0.85 * headA)})`;
          ctx.fillText(head, s.x, s.y);
        }

        s.y += s.speed;
        if (s.y > height + s.length * 14) {
          s.y = -Math.random() * 40;
          s.speed = 1.1 + Math.random() * 3.8;
        }
      }

      // Glyph clusters — small 2×2 to 3×4 groups of chars pulsing
      // at slow phases so the field reads as information architecture.
      for (const c of clusters) {
        const pulse =
          0.5 + 0.5 * Math.sin((t || 0) / c.period + c.phase);
        const a = c.alpha * pulse;
        if (a < 0.03) continue;
        ctx.fillStyle = `rgba(184,239,198,${a})`;
        for (let dxi = 0; dxi < c.w; dxi++) {
          for (let dyi = 0; dyi < c.h; dyi++) {
            const ch = CHARS[((c.phase * 97 + dxi * 7 + dyi * 13 + Math.floor(t / 900)) | 0) % CHARS.length];
            ctx.fillText(ch, c.x + dxi * 7, c.y + dyi * 12);
          }
        }
      }

      // Ambient dust — gentle twinkle, no motion.
      for (const d of dust) {
        const tw =
          0.5 + 0.5 * Math.sin((t || 0) / 1400 + d.twinkle);
        ctx.fillStyle = `rgba(126,212,147,${0.35 * d.alpha * tw})`;
        ctx.fillRect(d.x, d.y, 1.5, 1.5);
      }

      rafId = requestAnimationFrame(drawFrame);
    };

    const drawStatic = () => {
      ctx.clearRect(0, 0, width, height);
      ctx.font = "12px ui-monospace, SFMono-Regular, Menlo, monospace";
      for (const s of streams) {
        for (let k = 0; k < s.length; k++) {
          const y = height * ((k + 1) / (s.length + 1));
          const ch = CHARS[(Math.random() * CHARS.length) | 0];
          const a = s.alpha * (1 - k / s.length) * 0.35;
          ctx.fillStyle = `rgba(126,212,147,${a})`;
          ctx.fillText(ch, s.x, y);
        }
      }
      for (const d of dust) {
        ctx.fillStyle = `rgba(126,212,147,${0.25 * d.alpha})`;
        ctx.fillRect(d.x, d.y, 1.5, 1.5);
      }
    };

    const start = () => {
      measure();
      buildStreams();
      if (reducedMotion) drawStatic();
      else rafId = requestAnimationFrame(drawFrame);
    };

    const onResize = () => {
      if (rafId) cancelAnimationFrame(rafId);
      start();
    };

    start();
    window.addEventListener("resize", onResize);

    return () => {
      running = false;
      if (rafId) cancelAnimationFrame(rafId);
      window.removeEventListener("resize", onResize);
    };
  }, [reducedMotion, innerRatio, outerRatio]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      role="presentation"
      data-testid="northstar-matrix-field"
      className="absolute inset-0 pointer-events-none"
      style={{ zIndex: 1 }}
    />
  );
}
